import type { TRBLNumberCOORDS } from '../types';

/**
 * Parse a padding/margin string into TRBLNumberCOORDS format [top, right, bottom, left]
 *
 * Supported formats:
 * - Single value: "10" or "10px" → [10, 10, 10, 10]
 * - Four values: "10,20,10,20" → [10, 20, 10, 20]
 *
 * @param value - Padding/margin string to parse
 * @param defaultValue - Default value to return if parsing fails
 * @returns Parsed padding as [top, right, bottom, left]
 */
export const parsePadding = (
  value: string | null | undefined,
  defaultValue: TRBLNumberCOORDS,
): TRBLNumberCOORDS => {
  if (!value || value.trim() === '') {
    return defaultValue;
  }

  const trimmed = value.trim();

  // Handle comma-separated 4-value format: "10,20,10,20"
  if (trimmed.includes(',')) {
    const parts = trimmed.split(',').map((s) => parseNumericValue(s.trim()));

    if (parts.length === 4 && parts.every((p) => !isNaN(p))) {
      return parts as TRBLNumberCOORDS;
    }

    return defaultValue;
  }

  // Handle single value: "10" or "10px"
  const num = parseNumericValue(trimmed);
  if (!isNaN(num)) {
    return [num, num, num, num];
  }

  return defaultValue;
};

/**
 * Parse a border radius string into TRBLNumberCOORDS format
 * Same as padding but with different semantic meaning
 */
export const parseBorderRadius = (
  value: string | null | undefined,
  defaultValue: TRBLNumberCOORDS,
): TRBLNumberCOORDS => {
  return parsePadding(value, defaultValue);
};

/**
 * Parse a numeric value, stripping units like "px"
 */
const parseNumericValue = (value: string): number => {
  // Remove common units
  const numericPart = value.replace(/px|em|rem|%/gi, '').trim();
  return parseFloat(numericPart);
};
